import React from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';

/**
 * Catches render/commit errors from any component below it and shows a
 * recoverable fallback instead of letting React unmount the whole tree to a
 * blank white page. The error text is shown so a user screenshot identifies
 * the failure without needing DevTools.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Uncaught render error:', error, errorInfo?.componentStack);
    this.setState({ errorInfo });
  }

  render() {
    const { error, errorInfo } = this.state;

    if (!error) {
      return this.props.children;
    }

    const componentStack = errorInfo?.componentStack
      ? errorInfo.componentStack.split('\n').filter(Boolean).slice(0, 4).join('\n')
      : '';

    return (
      <Box
        sx={{
          flexGrow: 1,
          minHeight: '50vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 560, width: '100%', textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The page hit an unexpected error. Reloading usually fixes it — if it keeps
            happening, please screenshot this message and share it with support.
          </Typography>
          <Box
            component="pre"
            sx={{
              textAlign: 'left',
              bgcolor: 'grey.100',
              p: 1.5,
              borderRadius: 1,
              fontSize: 12,
              overflow: 'auto',
              maxHeight: 160,
              mb: 2,
              whiteSpace: 'pre-wrap'
            }}
          >
            {String(error)}
            {componentStack ? `\n${componentStack}` : ''}
          </Box>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Reload Page
          </Button>
        </Paper>
      </Box>
    );
  }
}

export default ErrorBoundary;
